import React from 'react';
import { useMount } from '@shared/utils/hooks/useMount';
import { useHotelBookingGuestSiteTelemetry } from '../../HotelBooking.telemetry';
import { useHotelBookingRedirectHealth, wrapWithHotelBookingRedirectProvider } from '../../HotelBooking.health';
import { useEventDisplayName } from '@shared/utils/eventInfo/useEventDisplayName';
import { useHotelSearchConfig } from './useHotelSearchConfig';
import { useQueryParamHelper } from '@shared/core/queryString';
import { HotelSearchContextParams } from '@shared/constants/accommodations';

// Sync import: bundled into this chunk to avoid a double lazy-load waterfall.
// See TRAV-2638 for performance analysis.
import { GuestAccommodationsConsole } from '@apps/stays/AccommodationsConsole/scenarios/guest/GuestAccommodationsConsole';
// Sync import: the cash-fund console reuses the same AccommodationsConsole monolith the guest
// console already ships in this chunk. Lazy-loading it into a separate chunk duplicates shared
// singletons (the AccommodationMapListSync React context), which breaks useAccommodationMapListSyncApi
// at runtime. The cash-fund-specific delta (banner + tile) is tiny, so there is no bundle benefit
// to splitting it out. (TRAV-3348)
import { CashFundAccommodationsConsole } from '@apps/stays/AccommodationsConsole/scenarios/cashFund/CashFundAccommodationsConsole';
import { AccommodationsConsoleLayoutSkeleton } from '@apps/stays/AccommodationsConsole/AccommodationsConsoleLayoutSkeleton';
import { useCashFundHotelSearch } from './useCashFundHotelSearch';

export const GuestHotelSearch = wrapWithHotelBookingRedirectProvider(({ eventHandle }: { eventHandle: string }) => {
  const { onLoadComplete } = useHotelBookingRedirectHealth();
  const telemetry = useHotelBookingGuestSiteTelemetry();
  const eventDisplayName = useEventDisplayName();
  const queryParamHelper = useQueryParamHelper();

  const { layout } = useHotelSearchConfig();

  const selectedHotelId = queryParamHelper.getValueString(HotelSearchContextParams.selectedHotel) || undefined;

  // Called unconditionally alongside the other hooks; the branches below run AFTER useMount.
  const { isCashFundVariant, isLoading } = useCashFundHotelSearch();

  useMount(() => {
    onLoadComplete();
    telemetry.hotelSearch.enter();
  });

  // `isLoading` is only ever true for the param-eligible cohort while the gating decision
  // resolves. Hold a full-footprint skeleton (not the guest console) to avoid a double-mount
  // + data refetch + flash when we then swap to the cash-fund console.
  if (isLoading) {
    return <AccommodationsConsoleLayoutSkeleton />;
  }

  if (isCashFundVariant) {
    return (
      <CashFundAccommodationsConsole
        eventHandle={eventHandle}
        eventName={eventDisplayName}
        selectedHotelId={selectedHotelId}
        layout={layout}
      />
    );
  }

  // Unchanged fallback — stays sync-imported, no extra waterfall.
  return (
    <GuestAccommodationsConsole
      eventHandle={eventHandle}
      eventName={eventDisplayName}
      selectedHotelId={selectedHotelId}
      layout={layout}
    />
  );
});
